import { ProcessRunState, ActivityRunState } from '@robocorp/robocloud-orchestrator-interface';
import { WorkItemState, WorkItemStateShort } from '~/types/resources';

const swapKeyValue = <T extends Record<string, string> = Record<string, string>>(
  record: T,
): { [K in keyof T as T[K]]: K } => {
  const swapped = Object.entries(record).map(([key, value]) => [value, key]);
  return Object.fromEntries(swapped);
};

const externalWorkItemStateMap = {
  [WorkItemState.New]: 'new',
  [WorkItemState.Pending]: 'pending',
  [WorkItemState.Registered]: 'registered',
  [WorkItemState.InProgress]: 'in_progress',
  [WorkItemState.Failed]: 'failed',
  [WorkItemState.Completed]: 'done',
  [WorkItemState.Output]: 'output',
} as const;
const internalWorkItemStateMap = swapKeyValue(externalWorkItemStateMap);

type ExternalWorkItemState =
  (typeof externalWorkItemStateMap)[keyof typeof externalWorkItemStateMap];

const externalWorkItemStateShortMap = {
  [WorkItemStateShort.New]: externalWorkItemStateMap[WorkItemState.New],
  [WorkItemStateShort.Pending]: externalWorkItemStateMap[WorkItemState.Pending],
  [WorkItemStateShort.InProgress]: externalWorkItemStateMap[WorkItemState.InProgress],
  [WorkItemStateShort.Failed]: externalWorkItemStateMap[WorkItemState.Failed],
  [WorkItemStateShort.Completed]: externalWorkItemStateMap[WorkItemState.Completed],
  [WorkItemStateShort.Output]: externalWorkItemStateMap[WorkItemState.Output],
} as const;
const internalWorkItemShortStateMap = swapKeyValue(externalWorkItemStateShortMap);

export type ExternalWorkItemShortState =
  (typeof externalWorkItemStateShortMap)[keyof typeof externalWorkItemStateShortMap];

const externalProcessRunStateMap = {
  [ProcessRunState.New]: 'new',
  [ActivityRunState.AwaitingRuntime]: 'awaiting_worker',
  [ProcessRunState.InProgress]: 'in_progress',
  [ProcessRunState.Pending]: 'pending',
  [ProcessRunState.Stopping]: 'stopping',
  [ProcessRunState.Complete]: 'completed',
} as const;
const internalProcessRunStateMap = swapKeyValue(externalProcessRunStateMap);

type ExternalProcessRunState =
  (typeof externalProcessRunStateMap)[keyof typeof externalProcessRunStateMap];

const externalStepRunStateMap = {
  [ActivityRunState.New]: 'new',
  [ActivityRunState.AwaitingRuntime]: 'awaiting_worker',
  [ActivityRunState.InProgress]: 'in_progress',
  [ActivityRunState.Complete]: 'completed',
} as const;
const internalStepRunStateMap = swapKeyValue(externalStepRunStateMap);

export type ExternalActivityRunState =
  (typeof externalStepRunStateMap)[keyof typeof externalStepRunStateMap];

export const getExternalWorkItemState = (state: WorkItemState): ExternalWorkItemState =>
  externalWorkItemStateMap[state] ?? state;
export const getExternalWorkItemStates = (states: WorkItemState[]): ExternalWorkItemState[] =>
  states.map(getExternalWorkItemState);
export const getInternalWorkItemState = (state: ExternalWorkItemState): WorkItemState =>
  internalWorkItemStateMap[state] ?? state;
export const getInternalWorkItemStates = (states: ExternalWorkItemState[]): WorkItemState[] =>
  states.map(getInternalWorkItemState);

export const getExternalWorkItemShortState = (
  state: WorkItemStateShort,
): ExternalWorkItemShortState => externalWorkItemStateShortMap[state] ?? state;
export const getExternalWorkItemShortStates = (
  states: WorkItemStateShort[],
): ExternalWorkItemShortState[] => states.map(getExternalWorkItemShortState);
export const getInternalWorkItemShortState = (
  state: ExternalWorkItemShortState,
): WorkItemStateShort => internalWorkItemShortStateMap[state] ?? state;
export const getInternalWorkItemShortStates = (
  states: ExternalWorkItemShortState[],
): WorkItemStateShort[] => states.map(getInternalWorkItemShortState);

export const getExternalProcessRunState = (state: ProcessRunState): ExternalProcessRunState =>
  externalProcessRunStateMap[state] ?? state;
export const getExternalProcessRunStates = (states: ProcessRunState[]): ExternalProcessRunState[] =>
  states.map(getExternalProcessRunState);
export const getInternalProcessRunState = (state: ExternalProcessRunState): ProcessRunState =>
  (internalProcessRunStateMap[state] as ProcessRunState) ?? state;
export const getInternalProcessRunStates = (states: ExternalProcessRunState[]): ProcessRunState[] =>
  states.map(getInternalProcessRunState);

export const getExternalStepRunState = (state: ActivityRunState): ExternalActivityRunState =>
  externalStepRunStateMap[state] ?? state;
export const getExternalStepRunStates = (states: ActivityRunState[]): ExternalActivityRunState[] =>
  states.map(getExternalStepRunState);
export const getInternalStepRunState = (state: ExternalActivityRunState): ActivityRunState =>
  internalStepRunStateMap[state] ?? state;
export const getInternalStepRunStates = (states: ExternalActivityRunState[]): ActivityRunState[] =>
  states.map(getInternalStepRunState);
